import { CommonModule, DOCUMENT } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';
import { PaymentsService } from '../../services/payments.service';
import { EventsService } from '../../services/events.service';
import { ToastService } from '../../services/toast.service';

type TicketStatus = 'assigned' | 'available' | 'cancelled' | 'not_found';

@Component({
  selector: 'app-ticket-validation',
  imports: [CommonModule, RouterModule],
  templateUrl: './ticket-validation.component.html',
  styles: `
    .ticket-card {
      border: 1px solid #dfeae7;
      border-radius: 16px;
      box-shadow: 0 12px 24px rgba(1, 68, 55, 0.08);
    }
  `
})
export class TicketValidationComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly paymentsService = inject(PaymentsService);
  private readonly eventsService = inject(EventsService);
  private readonly toast = inject(ToastService);
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly document = inject(DOCUMENT);

  loading = true;
  status: TicketStatus = 'not_found';
  serial = '';
  eventId?: number;
  eventTitle?: string;

  ngOnInit(): void {
    this.setSeo();
    this.route.paramMap.subscribe((params) => {
      const token = params.get('token');
      if (!token) {
        this.loading = false;
        this.status = 'not_found';
        return;
      }
      this.validate(token);
    });
  }

  get badgeClass(): string {
    if (this.status === 'assigned') return 'bg-success';
    if (this.status === 'available') return 'bg-warning text-dark';
    if (this.status === 'cancelled') return 'bg-danger';
    return 'bg-secondary';
  }

  get statusLabel(): string {
    if (this.status === 'assigned') return 'Boleto válido';
    if (this.status === 'available') return 'Pendiente de asignación';
    if (this.status === 'cancelled') return 'Boleto cancelado';
    return 'Boleto no encontrado';
  }

  private validate(token: string) {
    this.loading = true;
    this.paymentsService.validateEventTicket(token).subscribe({
      next: (res: { data?: any; Data?: any }) => {
        const payload = res?.data || res?.Data;
        this.status = (payload?.status || 'not_found') as TicketStatus;
        this.serial = payload?.serial || '';
        this.eventId = payload?.event_id;
        this.eventTitle = payload?.event_title || payload?.event_name || payload?.eventTitle || payload?.event?.title || '';
        if (!this.eventTitle && this.eventId) {
          this.loadEventTitle(this.eventId);
        }
        this.loading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.loading = false;
        this.status = 'not_found';
        this.toast.error(
          error?.error?.message_error || error?.error?.MessageError || 'No se pudo validar el boleto'
        );
      }
    });
  }

  private loadEventTitle(eventId: number): void {
    this.eventsService.get(eventId).subscribe({
      next: (res: { data?: any; Data?: any }) => {
        const eventData = res?.data || res?.Data;
        this.eventTitle = eventData?.title || '';
      },
      error: () => {
        this.eventTitle = '';
      }
    });
  }

  private setSeo() {
    const pageTitle = 'Validación de boleto | Summit UDEP 2026';
    const description = 'Verifica la validez del boleto escaneando el código QR';
    const canonicalUrl = `${this.document.location?.origin || ''}/ticket`;

    this.title.setTitle(pageTitle);
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ name: 'robots', content: 'index, follow' });
    this.meta.updateTag({ property: 'og:title', content: pageTitle });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:url', content: canonicalUrl });
  }
}
